import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Space, message, Tabs, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import PermissionsTable from '../../Component/PermissionsTable';
import { InfiniteSelect } from '../../Component/InfiniteSelect';
import NameByLang from '../../Component/nameByLang';
// Styles imported from parent

const { TabPane } = Tabs;
const { TextArea } = Input;

interface RoleModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  role: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const RoleModal: React.FC<RoleModalProps> = ({ visible, mode, role, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [modelsKey, setModelsKey] = useState(0); // Key to force InfiniteSelect re-render
  const [modelsInitialOptions, setModelsInitialOptions] = useState<any[]>([]); // Store initial options for InfiniteSelect
  const [isHydrated, setIsHydrated] = useState(false); // Guard to prevent re-hydration after save
  const [currentFilterModels, setCurrentFilterModels] = useState<any[]>([]); // Track models for PermissionsTable filter

  // Note: InfiniteSelect handles data fetching internally, so we don't need to fetch here
  // But we still need roleDetails for initialOptions

  // Fetch role details if editing
  // Refetch when switching to permissions or models tabs to get latest data
  const { data: roleDetails, refetch: refetchRoleDetails } = useQuery({
    queryKey: [`/api/admin/roles/${role?.id}`],
    queryFn: () => Api.get(`/api/admin/roles/${role?.id}`),
    enabled: visible && mode === 'edit' && !!role?.id,
  });

  // Refetch role details when switching tabs (always refetch to get latest data)
  useEffect(() => {
    if (visible && mode === 'edit' && role?.id && isHydrated) {
      // Refetch whenever tab changes to ensure we have latest data after any updates
      // This ensures fresh data when switching tabs after adding/removing models
      refetchRoleDetails().catch((error) => {
        console.error('Error refetching role details:', error);
      });
    }
  }, [activeTab, visible, mode, role?.id, isHydrated, refetchRoleDetails]);

  // Get models from currentFilterModels state (updated from API)
  const filterByModelsMemo = useMemo(() => {
    return currentFilterModels;
  }, [currentFilterModels]);

  // Create role mutation
  const createRoleMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/roles', data),
    onSuccess: () => {
      message.success(t('roles.addSuccess', 'Role created successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage = error?.response?.data?.msg || t('common.error', 'Error creating role');
        message.error(errorMessage);
      }
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: (data: any) => Api.put(`/api/admin/roles/${role?.id}`, data),
    onSuccess: () => {
      message.success(t('roles.updateSuccess', 'Role updated successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage = error?.response?.data?.msg || t('common.error', 'Error updating role');
        message.error(errorMessage);
      }
    },
  });

  // Assign permissions mutation
  const assignPermissionsMutation = useMutation({
    mutationFn: (permissionIds: number[]) =>
      Api.post(`/api/admin/roles/${role?.id}/assign-permissions`, { permissions: permissionIds }),
    onSuccess: async () => {
      message.success(t('roles.permissionsAssigned', 'Permissions assigned successfully'));
      // Refetch role details to get updated permissions
      try {
        const response = await refetchRoleDetails();
        const roleData = response?.data?.data?.role;
        if (roleData) {
          const permissionIds = roleData.permissions?.map((p: any) => p.id) || [];
          setCurrentPermissions(permissionIds);
          form.setFieldsValue({ permissions: permissionIds });
        }
      } catch (error) {
        console.error('Error refetching role details after permission assignment:', error);
      }
      // Invalidate role queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/admin/roles'] });
      queryClient.invalidateQueries({ queryKey: [`/api/admin/roles/${role?.id}`] });
      onSuccess?.();
    },
    onError: (error: any) => {
      console.error('Error assigning permissions:', error);
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || error?.response?.data?.msg;
        message.error(Array.isArray(errors) ? errors.join(', ') : errors || 'Validation error');
      } else {
        message.error(
          error?.response?.data?.msg || t('common.error', 'Error assigning permissions')
        );
      }
    },
  });

  // Assign models mutation
  const assignModelsMutation = useMutation({
    mutationFn: (modelIds: number[]) =>
      Api.post(`/api/admin/roles/${role?.id}/assign-models`, { models: modelIds }),
    onSuccess: async () => {
      message.success(t('roles.modelsAssigned', 'Models assigned successfully'));

      // Refetch role details immediately to get updated models from API
      try {
        const response = await refetchRoleDetails();
        const roleData = response?.data?.data?.role;
        if (roleData) {
          const menuModels = roleData.menu_models || roleData.menuModels || [];

          // Update currentFilterModels for PermissionsTable
          setCurrentFilterModels(menuModels);
          // Update modelsInitialOptions for InfiniteSelect
          setModelsInitialOptions(menuModels);

          // Update form with fresh data from API
          const modelsValue = menuModels.map((m: any) => ({
            label: (
              <NameByLang
                name_en={m.name_en}
                name_ar={m.name_ar}
                name={m.name || m.key || `Model ${m.id}`}
              />
            ),
            value: m.id,
            item: m,
          }));

          form.setFieldsValue({
            models: modelsValue,
          });

          // Force InfiniteSelect reset
          setModelsKey((k) => k + 1);
        }
      } catch (error) {
        console.error('Error refetching role details after model assignment:', error);
      }

      // Invalidate role query to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/roles/${role?.id}`],
      });

      onSuccess?.();
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Convert InfiniteSelect labelInValue format to simple IDs for create/update
      const submitValues = { ...values };
      // Convert models to array of IDs
      if (submitValues.models && Array.isArray(submitValues.models)) {
        submitValues.models = submitValues.models.map((m: any) =>
          typeof m === 'object' && m.value ? m.value : m
        );
      }
      if (mode === 'create') {
        await createRoleMutation.mutateAsync(submitValues);
      } else if (mode === 'edit') {
        await updateRoleMutation.mutateAsync(submitValues);
      }
    } finally {
      setLoading(false);
    }
  };

  // Store current permissions for submission
  const [currentPermissions, setCurrentPermissions] = useState<number[]>([]);

  const handlePermissionsSubmit = async () => {
    // Use currentPermissions state instead of form value
    const permissionIds = currentPermissions;
    if (mode === 'edit' && role?.id) {
      setLoading(true);
      try {
        await assignPermissionsMutation.mutateAsync(permissionIds);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePermissionsChange = (permissionIds: number[]) => {
    setCurrentPermissions(permissionIds);
    form.setFieldsValue({ permissions: permissionIds });
  };

  const handleModelsChange = (value: any) => {
    // This ensures the form is updated when user changes models
    form.setFieldsValue({ models: value });
  };

  const handleModelsSubmit = async () => {
    const modelsValue = form.getFieldValue('models') || [];
    // InfiniteSelect returns labelInValue format: [{ label: string, value: number }, ...]
    // Extract just the IDs
    const modelIds = Array.isArray(modelsValue)
      ? modelsValue.map((m: any) => (typeof m === 'object' && m.value ? m.value : m))
      : [];
    if (mode === 'edit' && role?.id) {
      setLoading(true);
      try {
        await assignModelsMutation.mutateAsync(modelIds);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setActiveTab('basic');
    setModelsInitialOptions([]);
    setCurrentFilterModels([]);
    setCurrentPermissions([]);
    setModelsKey(0);
    setIsHydrated(false); // Reset hydration guard
    onClose();
  };

  // Set form values when editing (only once when modal opens)
  useEffect(() => {
    if (!visible || mode !== 'edit' || isHydrated) return;

    const roleData = roleDetails?.data?.data?.role || role;
    if (!roleData) return;

    const menuModels = roleData.menu_models || roleData.menuModels || [];

    const permissionIds = roleData.permissions?.map((p: any) => p.id) || [];
    const modelsValue = Array.isArray(menuModels)
      ? menuModels.map((m: any) => ({
          label: (
            <NameByLang
              name_en={m.name_en}
              name_ar={m.name_ar}
              name={m.name || m.key || `Model ${m.id}`}
            />
          ),
          value: m.id,
          item: m,
        }))
      : [];

    // Set initial options for InfiniteSelect
    setModelsInitialOptions(menuModels || []);
    // Set current filter models for PermissionsTable
    setCurrentFilterModels(menuModels || []);
    // Set current permissions for submission
    setCurrentPermissions(permissionIds);

    form.setFieldsValue({
      name: roleData.name,
      display_name_en: roleData.display_name_en,
      display_name_ar: roleData.display_name_ar,
      description_en: roleData.description_en,
      description_ar: roleData.description_ar,
      permissions: permissionIds,
      models: modelsValue,
    });

    setIsHydrated(true); // Lock hydration - prevent re-running
  }, [visible, mode, roleDetails, role, isHydrated, form]);

  // Update selectedModels and form values when roleDetails changes after refetch (e.g., on tab switch)
  useEffect(() => {
    if (!visible || mode !== 'edit' || !isHydrated) return;

    const roleData = roleDetails?.data?.data?.role;
    if (!roleData) return;

    const menuModels = roleData.menu_models || roleData.menuModels || [];
    const permissionIds = roleData.permissions?.map((p: any) => p.id) || [];

    // Update modelsInitialOptions from API data
    setModelsInitialOptions(menuModels || []);
    // Update current filter models for PermissionsTable
    setCurrentFilterModels(menuModels || []);
    // Update current permissions for submission
    setCurrentPermissions(permissionIds);

    // Update form models value
    const modelsValue = Array.isArray(menuModels)
      ? menuModels.map((m: any) => ({
          label: (
            <NameByLang
              name_en={m.name_en}
              name_ar={m.name_ar}
              name={m.name || m.key || `Model ${m.id}`}
            />
          ),
          value: m.id,
          item: m,
        }))
      : [];

    // Update form values based on active tab
    if (activeTab === 'permissions' || activeTab === 'models') {
      form.setFieldsValue({
        models: modelsValue,
        permissions: permissionIds,
      });

      // Force InfiniteSelect reset to reflect new models
      setModelsKey((k) => k + 1);
    } else if (activeTab === 'basic') {
      // Update basic info fields as well
      form.setFieldsValue({
        name: roleData.name,
        display_name_en: roleData.display_name_en,
        display_name_ar: roleData.display_name_ar,
        description_en: roleData.description_en,
        description_ar: roleData.description_ar,
      });
    }
  }, [roleDetails, activeTab, visible, mode, isHydrated, form]);

  // Reset hydration when modal closes or switches to create mode
  useEffect(() => {
    if (!visible || mode === 'create') {
      setIsHydrated(false);
      if (mode === 'create') {
        form.resetFields();
        setModelsInitialOptions([]);
        setCurrentFilterModels([]);
        setCurrentPermissions([]);
        setModelsKey(0);
      }
    }
  }, [visible, mode, form]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return t('roles.addNew', 'Add New Role');
      case 'edit':
        return t('roles.edit', 'Edit Role');
      default:
        return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1400}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('roles.basicInfo', 'Basic Information')} key="basic">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label={t('roles.form.name', 'Name')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input
                placeholder={t('roles.form.namePlaceholder', 'Enter role name (e.g., manager)')}
              />
            </Form.Item>

            <Form.Item
              name="display_name_en"
              label={t('roles.form.displayNameEn', 'Display Name (English)')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input
                placeholder={t(
                  'roles.form.displayNameEnPlaceholder',
                  'Enter display name in English'
                )}
              />
            </Form.Item>

            <Form.Item
              name="display_name_ar"
              label={t('roles.form.displayNameAr', 'Display Name (Arabic)')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input
                placeholder={t(
                  'roles.form.displayNameArPlaceholder',
                  'Enter display name in Arabic'
                )}
              />
            </Form.Item>

            <Form.Item
              name="description_en"
              label={t('roles.form.descriptionEn', 'Description (English)')}
            >
              <TextArea
                rows={4}
                placeholder={t(
                  'roles.form.descriptionEnPlaceholder',
                  'Enter description in English'
                )}
              />
            </Form.Item>

            <Form.Item
              name="description_ar"
              label={t('roles.form.descriptionAr', 'Description (Arabic)')}
            >
              <TextArea
                rows={4}
                placeholder={t(
                  'roles.form.descriptionArPlaceholder',
                  'Enter description in Arabic'
                )}
              />
            </Form.Item>

            {mode === 'create' && (
              <Form.Item name="models" label={t('dataTranslations.models.Models', 'Models')}>
                <InfiniteSelect
                  queryKey="/api/admin/models"
                  mode="multiple"
                  placeholder={t('roles.form.selectModels', 'Select models')}
                  style={{ width: '100%' }}
                  labelRender={(option: any) => {
                    const item = 'item' in option ? option.item : option;
                    return (
                      <NameByLang
                        name_en={item?.name_en}
                        name_ar={item?.name_ar}
                        name={item?.name}
                      />
                    );
                  }}
                  valueField="id"
                  searchField="name"
                />
              </Form.Item>
            )}

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {mode === 'create' ? t('common.add', 'Add') : t('common.update', 'Update')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        {mode === 'edit' && (
          <>
            <TabPane tab={t('roles.permissions', 'Permissions')} key="permissions">
              <Form form={form} layout="vertical">
                <Form.Item
                  name="permissions"
                  label={t('roles.form.assignPermissions', 'Assign Permissions')}
                >
                  {filterByModelsMemo.length === 0 ? (
                    <Alert
                      type="info"
                      showIcon
                      message={t(
                        'roles.form.assignModelsFirst',
                        'Assign menu models on the Models tab first. Permission rows only appear for models linked to this role.'
                      )}
                    />
                  ) : null}
                  <PermissionsTable
                    key={`permissions-table-${roleDetails?.data?.data?.role?.id}-${filterByModelsMemo.length}-${currentPermissions.join('-')}`}
                    selectedPermissions={currentPermissions}
                    onChange={handlePermissionsChange}
                    initialPermissions={roleDetails?.data?.data?.role?.permissions || []}
                    filterByModels={filterByModelsMemo}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                    <Button type="primary" onClick={handlePermissionsSubmit} loading={loading}>
                      {t('roles.assignPermissions', 'Assign Permissions')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab={t('roles.models', 'Models')} key="models">
              <Form form={form} layout="vertical">
                <Form.Item name="models" label={t('roles.form.assignModels', 'Assign Models')}>
                  <InfiniteSelect
                    key={`models-select-${modelsKey}`}
                    queryKey="/api/admin/models"
                    mode="multiple"
                    placeholder={t('roles.form.selectModels', 'Select models')}
                    style={{ width: '100%' }}
                    onChange={(value: any) => {
                      handleModelsChange(value);
                    }}
                    labelRender={(option: any) => {
                      const item = 'item' in option ? option.item : option;
                      return (
                        <NameByLang
                          name_en={item?.name_en}
                          name_ar={item?.name_ar}
                          name={item?.name}
                        />
                      );
                    }}
                    valueField="id"
                    searchField="name"
                    initialOptions={modelsInitialOptions}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                    <Button type="primary" onClick={handleModelsSubmit} loading={loading}>
                      {t('roles.assignModels', 'Assign Models')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>
          </>
        )}
      </Tabs>
    </Modal>
  );
};

export default RoleModal;
